/**
 * React Query hooks for Content Packs
 *
 * Provides data fetching and mutation hooks for content pack management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { contentPacksApi } from '@/lib/api/contentPacks';
import type { ContentPackCreate, ContentPackUpdate, ItemRenderMetadata } from '@/lib/api/contentPacks';

// ============================================================================
// Query Keys
// ============================================================================

export const contentPackKeys = {
  all: ['content-packs'] as const,
  lists: () => [...contentPackKeys.all, 'list'] as const,
  list: (type?: string, status?: string) => [...contentPackKeys.lists(), { type, status }] as const,
  details: () => [...contentPackKeys.all, 'detail'] as const,
  detail: (id: string) => [...contentPackKeys.details(), id] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for listing content packs with optional type/status filters
 */
export function useContentPackList(type?: string, status?: string) {
  return useQuery({
    queryKey: contentPackKeys.list(type, status),
    queryFn: () => contentPacksApi.list(type, status),
  });
}

/**
 * Hook for getting a single content pack by ID
 */
export function useContentPack(packId: string) {
  return useQuery({
    queryKey: contentPackKeys.detail(packId),
    queryFn: () => contentPacksApi.get(packId),
    enabled: !!packId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for creating a new content pack
 */
export function useCreateContentPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ContentPackCreate) => contentPacksApi.create(data),
    onSuccess: (pack) => {
      queryClient.invalidateQueries({ queryKey: contentPackKeys.lists() });
      toast.success(`内容包 "${pack.display_name}" 已创建`);
    },
    onError: (error: Error) => {
      toast.error(`创建失败: ${error.message}`);
    },
  });
}

/**
 * Hook for updating an existing content pack
 */
export function useUpdateContentPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContentPackUpdate }) =>
      contentPacksApi.update(id, data),
    onSuccess: (pack) => {
      queryClient.invalidateQueries({ queryKey: contentPackKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contentPackKeys.detail(pack.id) });
      toast.success(`内容包 "${pack.display_name}" 已更新`);
    },
    onError: (error: Error) => {
      toast.error(`更新失败: ${error.message}`);
    },
  });
}

/**
 * Hook for deleting a content pack
 */
export function useDeleteContentPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (packId: string) => contentPacksApi.delete(packId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentPackKeys.lists() });
      toast.success('内容包已删除');
    },
    onError: (error: Error) => {
      toast.error(`删除失败: ${error.message}`);
    },
  });
}

/**
 * Hook for uploading items to a content pack
 */
export function useUploadItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packId, files, itemType }: { packId: string; files: File[]; itemType?: string }) =>
      contentPacksApi.uploadItems(packId, files, itemType),
    onSuccess: (_items, variables) => {
      queryClient.invalidateQueries({ queryKey: contentPackKeys.detail(variables.packId) });
      queryClient.invalidateQueries({ queryKey: contentPackKeys.lists() });
      toast.success(`已上传 ${_items.length} 个文件`);
    },
    onError: (error: Error) => {
      toast.error(`上传失败: ${error.message}`);
    },
  });
}

/**
 * Hook for updating item metadata
 */
export function useUpdateItemMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packId, itemId, metadata }: { packId: string; itemId: string; metadata: ItemRenderMetadata }) =>
      contentPacksApi.updateItemMetadata(packId, itemId, metadata),
    onSuccess: (_item, variables) => {
      queryClient.invalidateQueries({ queryKey: contentPackKeys.detail(variables.packId) });
      toast.success('文件元数据已更新');
    },
    onError: (error: Error) => {
      toast.error(`更新失败: ${error.message}`);
    },
  });
}

/**
 * Hook for deleting a single item from a content pack
 */
export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packId, itemId }: { packId: string; itemId: string }) =>
      contentPacksApi.deleteItem(packId, itemId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: contentPackKeys.detail(variables.packId) });
      queryClient.invalidateQueries({ queryKey: contentPackKeys.lists() });
      toast.success('文件已删除');
    },
    onError: (error: Error) => {
      toast.error(`删除失败: ${error.message}`);
    },
  });
}

/**
 * Hook for one-time filesystem migration
 */
export function useMigrateFilesystem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => contentPacksApi.migrateFilesystem(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: contentPackKeys.lists() });
      const parts = []
      if (result.migrated > 0) parts.push(`新建 ${result.migrated} 个`)
      if (result.updated > 0) parts.push(`更新 ${result.updated} 个`)
      if (result.errors > 0) parts.push(`失败 ${result.errors} 个`)
      toast.success(`迁移完成: ${parts.join('，') || '无变更'}`);
    },
    onError: (error: Error) => {
      toast.error(`迁移失败: ${error.message}`);
    },
  });
}
